import { Box, TableCell, TableRow } from '@mui/material';
import Tooltip, { TooltipProps } from '@mui/material/Tooltip';

import { formatDate, formatTime } from '../../helpers/formatDate';
import { LedgerEntry } from '../../types';
import { numberToUSDCurrency } from '../../helpers/numberToUSDCurrency';
import { StatusChip } from '../../components/StatusChip';
import FormLinkButton from '../../components/buttons/FormLinkButton';
import { palette } from '../../palette';
import { LedgerCategoryMap, LedgerStatusMap } from '../PaymentsView/SettlementsView/constants';

interface CaseLedgerTableDataRowProps {
  tableData: LedgerEntry[];
}

const tooltipProps: TooltipProps['componentsProps'] = {
  tooltip: {
    sx: (theme) => ({
      backgroundColor: theme.palette.common.white,
      color: theme.palette.primary.light,
      borderRadius: '5px',
      boxShadow: '3px',
      fontFamily: 'Inter',
      fontSize: '13px',
      fontWeight: 500,
      padding: '5px',
      maxWidth: '250px',
    }),
  },
};

// Credit/debit columns show a dash instead of $0.00 for the side that does not apply
const formatAmount = (value?: number | null) => (value ? numberToUSDCurrency(value) : '-');

// SETTLEMENT_CREDIT -> Settlement Credit; fallback label for category and status
const formatEnumLabel = (value?: string) =>
  value
    ? value
      .toLowerCase()
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
    : '-';

// Category holds the settlement type (e.g. STANDARD_PIP -> Settlement + PIP); unknown values fall back to title case
export const getLedgerCategoryLabel = (category?: string) =>
  (category && LedgerCategoryMap[category]) || formatEnumLabel(category);

const CaseLedgerTableDataRow = ({ tableData }: CaseLedgerTableDataRowProps) => {
  const formattedLedgerList = tableData.map((ledger: LedgerEntry, index) => ({
    key: `${ledger.sourceType}-${ledger.sourceId ?? ''}-${ledger.sqNo ?? ''}-${index}`,
    // Lien reimbursement rows link to the deduction's Recovery tab; other rows link to their settlement
    linkPath:
      ledger.category === 'LIEN_REIMBURSEMENT' && ledger.deductionId
        ? `/payments/deduction/${ledger.deductionId}/recovery`
        : ledger.settlementId
          ? `/payments/settlement/${ledger.settlementId}`
          : null,
    status: ledger.status,
    settlementId: ledger.settlementId,
    // Lien reimbursement rows show the date only, without the time tooltip
    time: ledger.date && ledger.category !== 'LIEN_REIMBURSEMENT' ? formatTime(ledger.date) : null,
    rowFields: {
      date: ledger.date ? formatDate(ledger.date) : '-',
      description: ledger.description || '-',
      settlement: getLedgerCategoryLabel(ledger.category),
      credit: formatAmount(ledger.credit),
      debit: formatAmount(ledger.debit),
      balance: numberToUSDCurrency(ledger.balance),
      status: formatEnumLabel(ledger.status),
    },
  }));

  return (
    <>
      {formattedLedgerList.map((tableItem) => (
        <TableRow key={tableItem.key}>
          {Object.entries(tableItem.rowFields).map(([fieldName, value]) => (
            // Settlement label + ID + link exceeds the shared 200px cell cap, so let this column grow
            <TableCell key={fieldName} style={fieldName === 'settlement' ? { maxWidth: 'none' } : undefined}>
              {fieldName === 'date' && tableItem.time ? (
                <Tooltip title={tableItem.time} arrow componentsProps={tooltipProps}>
                  <span>{value}</span>
                </Tooltip>
              ) : fieldName === 'description' ? (
                <Tooltip title={String(value)} arrow componentsProps={tooltipProps}>
                  <span>{value}</span>
                </Tooltip>
              ) : fieldName === 'settlement' && tableItem.linkPath ? (
                <Box display="flex" alignItems="center" gap="6px">
                  <span>{value}</span>
                  {tableItem.settlementId && <span>{`#${tableItem.settlementId}`}</span>}
                  <Box
                    component="span"
                    display="inline-flex"
                    sx={{
                      '& > a': { marginBottom: 0 },
                      '& svg': { transition: 'transform 0.15s' },
                      '&:hover svg': { transform: 'scale(1.1)' },
                    }}
                  >
                    <FormLinkButton path={tableItem.linkPath} color="#4aaac0" />
                  </Box>
                </Box>
              ) : (fieldName === 'credit' || fieldName === 'debit') && value !== '-' ? (
                <Box
                  component="span"
                  color={fieldName === 'credit' ? palette.success.main : palette.error.main}
                >
                  {value}
                </Box>
              ) : fieldName === 'status' && tableItem.status ? (
                <StatusChip
                  label={LedgerStatusMap[tableItem.status]?.label || String(value)}
                  color={LedgerStatusMap[tableItem.status]?.color || palette.text.disabled}
                />
              ) : (
                value
              )}
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
};

export default CaseLedgerTableDataRow;
